// Pure ground-footprint maths for the minimap's camera view box (minimap-design.md §4).
//
// The 3D camera looks at the board from above at an angle. Unprojecting the four NDC
// corners onto the y = 0 ground plane gives the world rectangle the player sees; corners
// whose ray points at or above the horizon (dy >= 0) never hit the ground, so the ray is
// clipped at `maxDistance` instead — otherwise a near-horizontal camera would produce an
// unbounded polygon. Pure over a minimal ray type so it tests without THREE.

/** A ray in world space: origin (ox, oy, oz), direction (dx, dy, dz). */
export interface Ray {
  readonly ox: number;
  readonly oy: number;
  readonly oz: number;
  readonly dx: number;
  readonly dy: number;
  readonly dz: number;
}

/**
 * Where the ray meets the ground plane y = 0, as (x, z) — or, when it points away from
 * the ground (dy >= 0) or the hit is beyond `maxDistance`, the ray's (x, z) at
 * `maxDistance` (its clipped extent). Null only for a ray parallel to the ground with a
 * zero horizontal component (degenerate).
 */
export function groundHit(r: Ray, maxDistance: number): readonly [number, number] | null {
  if (r.dy < -1e-9) {
    const t = -r.oy / r.dy;
    if (t <= maxDistance) return [r.ox + r.dx * t, r.oz + r.dz * t];
  }
  const horizontal = Math.hypot(r.dx, r.dz);
  if (horizontal < 1e-9) return null;
  const t = maxDistance / horizontal;
  return [r.ox + r.dx * t, r.oz + r.dz * t];
}

/**
 * The four ground points of a frustum (rays in NDC corner order), clipped by
 * `groundHit`. Points that could not be resolved are dropped; fewer than three leaves
 * no drawable polygon.
 */
export function footprintPolygon(
  rays: readonly Ray[],
  maxDistance: number,
): readonly (readonly [number, number])[] {
  const pts: (readonly [number, number])[] = [];
  for (const r of rays) {
    const hit = groundHit(r, maxDistance);
    if (hit) pts.push(hit);
  }
  return pts.length >= 3 ? pts : [];
}

/** Polygon equality at board-coordinate precision (the publisher publishes change-only). */
export function polygonEquals(
  a: readonly (readonly [number, number])[] | null,
  b: readonly (readonly [number, number])[] | null,
): boolean {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((p, i) => {
    const q = b[i];
    return q !== undefined && Math.abs(p[0] - q[0]) < 1e-3 && Math.abs(p[1] - q[1]) < 1e-3;
  });
}
